// @ts-nocheck
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, Edit, Mail, Calendar } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface AdvertiserProspect {
  id: string;
  company_name: string;
  contact_name: string;
  contact_email: string;
  industry_category: string;
  advertiser_tier: string;
  pipeline_stage: string;
  estimated_campaign_value: number;
  notes: string;
  created_at: string;
}

const pipelineStages = [
  { id: 'prospect', name: 'Prospect', color: 'bg-gray-100' },
  { id: 'media_kit_sent', name: 'Media Kit Sent', color: 'bg-blue-100' },
  { id: 'proposal_submitted', name: 'Proposal Submitted', color: 'bg-purple-100' },
  { id: 'negotiation', name: 'Negotiation', color: 'bg-yellow-100' },
  { id: 'contract_signed', name: 'Contract Signed', color: 'bg-green-100' },
  { id: 'campaign_live', name: 'Campaign Live', color: 'bg-emerald-100' },
  { id: 'renewal', name: 'Renewal', color: 'bg-orange-100' }
];

interface SortableProspectCardProps {
  prospect: AdvertiserProspect;
  onEdit: (prospect: AdvertiserProspect) => void;
  onViewDetails: (prospect: AdvertiserProspect) => void;
}

const SortableProspectCard: React.FC<SortableProspectCardProps> = ({
  prospect,
  onEdit,
  onViewDetails
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: prospect.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'local': return 'bg-blue-100 text-blue-800';
      case 'regional': return 'bg-purple-100 text-purple-800';
      case 'national': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="mb-3 cursor-grab hover:shadow-md transition-shadow"
    >
      <CardContent className="p-4">
        <div className="space-y-3">
          <div className="flex justify-between items-start">
            <div>
              <h4 className="font-semibold text-foreground">{prospect.company_name}</h4>
              <p className="text-sm text-muted-foreground">{prospect.contact_name}</p>
              <p className="text-xs text-muted-foreground">{prospect.contact_email}</p>
            </div>
            <Badge className={getTierColor(prospect.advertiser_tier)}>
              {prospect.advertiser_tier}
            </Badge>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Industry:</span>
              <span className="text-sm font-medium">{prospect.industry_category}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Est. Value:</span>
              <span className="text-sm font-semibold text-primary">
                {formatCurrency(prospect.estimated_campaign_value || 0)}
              </span>
            </div>
          </div>

          {prospect.notes && (
            <p className="text-xs text-muted-foreground bg-muted p-2 rounded">
              {prospect.notes.substring(0, 100)}...
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onViewDetails(prospect)}
              className="gap-1"
            >
              <Eye className="h-3 w-3" />
              View
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(prospect)}
              className="gap-1"
            >
              <Edit className="h-3 w-3" />
              Edit
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
            >
              <Mail className="h-3 w-3" />
              Email
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AdvertiserPipeline: React.FC = () => {
  const { profile } = useAuth();
  const [prospects, setProspects] = useState<AdvertiserProspect[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile?.id) {
      fetchProspects();
    }
  }, [profile?.id]);

  const fetchProspects = async () => {
    try {
      const { data, error } = await supabase
        .from('advertiser_prospects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProspects(data || []);
    } catch (error) {
      console.error('Error fetching advertiser prospects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const prospectId = active.id as string;
    const newStage = over.id as string;

    try {
      const { error } = await supabase
        .from('advertiser_prospects')
        .update({ pipeline_stage: newStage })
        .eq('id', prospectId);

      if (error) throw error;

      setProspects(prev => 
        prev.map(prospect => 
          prospect.id === prospectId 
            ? { ...prospect, pipeline_stage: newStage }
            : prospect
        )
      );
    } catch (error) {
      console.error('Error updating prospect stage:', error);
    }
  };

  const handleEditProspect = (prospect: AdvertiserProspect) => {
    // TODO: Open edit modal
    console.log('Edit prospect:', prospect);
  };

  const handleViewDetails = (prospect: AdvertiserProspect) => {
    // TODO: Open details modal
    console.log('View prospect details:', prospect);
  };

  const getProspectsForStage = (stage: string) => {
    return prospects.filter(prospect => prospect.pipeline_stage === stage);
  };

  const getStageTotal = (stage: string) => {
    return getProspectsForStage(stage).reduce(
      (sum, prospect) => sum + (prospect.estimated_campaign_value || 0), 0
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {pipelineStages.slice(0, 4).map((stage) => (
          <Card key={stage.id} className="animate-pulse">
            <CardHeader className="pb-3">
              <div className="h-6 bg-muted rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="h-20 bg-muted rounded"></div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {pipelineStages.map((stage) => {
          const stageProspects = getProspectsForStage(stage.id);
          const stageTotal = getStageTotal(stage.id);

          return (
            <Card key={stage.id} className={`${stage.color} min-h-[400px]`}>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex justify-between items-center">
                  <span>{stage.name}</span>
                  <Badge variant="secondary">{stageProspects.length}</Badge>
                </CardTitle>
                {stageTotal > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Total: {formatCurrency(stageTotal)}
                  </p>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                <SortableContext 
                  items={stageProspects.map(p => p.id)} 
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {stageProspects.map((prospect) => (
                      <SortableProspectCard
                        key={prospect.id}
                        prospect={prospect}
                        onEdit={handleEditProspect}
                        onViewDetails={handleViewDetails}
                      />
                    ))}
                    {stageProspects.length === 0 && (
                      <p className="text-center text-muted-foreground text-sm py-8">
                        No prospects in this stage
                      </p>
                    )}
                  </div>
                </SortableContext>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </DndContext>
  );
};

export default AdvertiserPipeline;